/**
 * Live WebSocket Connections Admin Page
 * 
 * Real-time monitoring of all WebSocket connections with hierarchical identity display
 * Shows connected users, devices, pages, subscriptions, and performance metrics
 * 
 * Features:
 * - Live connection updates via WebSocket subscription
 * - Hierarchical identity display (user → device → window → page)
 * - Real-time subscription monitoring
 * - Performance metrics and latency tracking
 * - Connection health status
 */

import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Loader } from '../../../components/ds';
import { WebSocketContext } from '../../../context/WebSocketProvider';
import { PageHeader } from '../../../components/ds';
import WebSocketManager from '../../../websocket/WebSocketManager';
import { useSnackbar } from '../../../contexts/SnackbarContext.js';

const LiveConnections = () => {
  const { isConnected } = useContext(WebSocketContext);
  const { showSnackbar } = useSnackbar();
  const [connectionsData, setConnectionsData] = useState({
    users: {},
    totalConnections: 0,
    totalUsers: 0,
    totalDevices: 0,
    totalWindows: 0,
    totalPages: 0,
    subscriptions: {},
    performanceMetrics: {
      avgLatency: 0,
      messageVolume: 0,
      errorRate: 0
    },
    totalInternalConnections: 0,
    totalExternalConnections: 0
  });
  const [adminPageId] = useState(() => WebSocketManager.generatePageId('admin_live_connections'));
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Unified handler for both admin response and live status messages
  const handleConnectionsUpdate = useCallback((msgOrData) => {
    // Accepts either a protocol message (from WebSocket) or a direct data object (from admin response)
    let data;
    if (msgOrData && typeof msgOrData === 'object' && ('t' in msgOrData || 'type' in msgOrData)) {
      // Protocol message
      data = msgOrData.d || {};
    } else {
      // Direct data (admin response)
      data = msgOrData?.data?.data || {};
    }
    if (!Array.isArray(data.user_metrics)) return;
    const metricsArr = data.user_metrics;
    // Calculate summary fields
    const totalConnections = metricsArr.length;
    const userIds = new Set();
    const deviceIds = new Set();
    const windowIds = new Set();
    const pageIds = new Set();
    const subscriptions = {};
    let missingDeviceId = false, missingWindowId = false, missingPageId = false;
    // Build minimal users/devices/windows/pages tree
    const users = {};
    // Track subscriptions per page for merging
    const pageSubscriptions = {};
    let totalInternalConnections = 0;
    let totalExternalConnections = 0;
    metricsArr.forEach(m => {
      if (m.user_id) userIds.add(m.user_id);
      if ('device_id' in m && m.device_id) deviceIds.add(m.device_id); else missingDeviceId = true;
      if ('window_id' in m && m.window_id) windowIds.add(m.window_id); else missingWindowId = true;
      if ('page_id' in m && m.page_id) pageIds.add(m.page_id); else missingPageId = true;
      if (Array.isArray(m.subscriptions)) {
        m.subscriptions.forEach(sub => {
          subscriptions[sub] = (subscriptions[sub] || 0) + 1;
        });
        // Merge subscriptions for each page_id
        if (m.page_id) {
          if (!pageSubscriptions[m.page_id]) pageSubscriptions[m.page_id] = new Set();
          m.subscriptions.forEach(sub => pageSubscriptions[m.page_id].add(sub));
        }
      }
      // Count internal/external connections
      if (m.client_type === 'internal' || (m.user_id && m.user_id.startsWith('service_'))) {
        totalInternalConnections++;
      } else {
        totalExternalConnections++;
      }
      // Build hierarchy
      if (m.user_id && m.device_id && m.window_id && m.page_id) {
        if (!users[m.user_id]) users[m.user_id] = { user_id: m.user_id, devices: {}, totalConnections: 0, lastSeen: m.last_activity };
        if (!users[m.user_id].devices[m.device_id]) users[m.user_id].devices[m.device_id] = { device_id: m.device_id, windows: {}, metadata: {}, totalConnections: 0 };
        if (!users[m.user_id].devices[m.device_id].windows[m.window_id]) users[m.user_id].devices[m.device_id].windows[m.window_id] = { window_id: m.window_id, pages: {}, totalConnections: 0 };
        // Merge subscriptions for this page
        const mergedSubs = Array.from(pageSubscriptions[m.page_id] || []);
        users[m.user_id].devices[m.device_id].windows[m.window_id].pages[m.page_id] = {
          page_id: m.page_id,
          connection_id: m.connection_id,
          connected_at: m.connected_at || 0,
          last_seen: m.last_activity,
          connection_type: m.client_type,
          subscriptions: mergedSubs,
          websocket_server: m.websocket_server || ''
        };
        users[m.user_id].totalConnections++;
        users[m.user_id].devices[m.device_id].totalConnections++;
        users[m.user_id].devices[m.device_id].windows[m.window_id].totalConnections++;
        if (m.last_activity > users[m.user_id].lastSeen) users[m.user_id].lastSeen = m.last_activity;
      } else {
        // Debug: log skipped metrics and why
        const missing = [];
        if (!m.user_id) missing.push('user_id');
        if (!m.device_id) missing.push('device_id');
        if (!m.window_id) missing.push('window_id');
        if (!m.page_id) missing.push('page_id');
        console.warn('[LiveConnections] Skipping metric from hierarchy due to missing fields:', missing, m);
      }
    });
    // Debug: print subscriptions for each page
    Object.values(users).forEach(u => {
      Object.values(u.devices).forEach(d => {
        Object.values(d.windows).forEach(w => {
          Object.entries(w.pages).forEach(([pid, pdata]) => {
            console.debug(`[LiveConnections] Page ${pid} subscriptions:`, pdata.subscriptions);
          });
        });
      });
    });
    // Performance metrics (from backend if present, else fallback)
    const perf = data.performanceMetrics || data.performance || {};
    const performanceMetrics = {
      avgLatency: typeof perf.avgLatency === 'number' ? perf.avgLatency : 0,
      messageVolume: typeof perf.messageVolume === 'number' ? perf.messageVolume : totalConnections,
      errorRate: typeof perf.errorRate === 'number' ? perf.errorRate : 0
    };
    setConnectionsData({
      users,
      totalConnections,
      totalUsers: userIds.size,
      totalDevices: deviceIds.size,
      totalWindows: windowIds.size,
      totalPages: pageIds.size,
      subscriptions,
      performanceMetrics,
      missingDeviceId,
      missingWindowId,
      missingPageId,
      totalInternalConnections,
      totalExternalConnections
    });
    setLastUpdate(new Date());
  }, []);

  // Request connections update from admin service (for initial load only)
  const requestConnectionsUpdate = useCallback(() => {
    if (!isConnected) {
      console.log('[LiveConnections] Cannot request update - WebSocket not connected');
      return;
    }
    if (!WebSocketManager.isAuthenticated) {
      console.log('[LiveConnections] Cannot request update - WebSocket not authenticated');
      return;
    }
    console.log('[LiveConnections] Requesting connections update with admin message');
    try {
      const adminMsg = {
        type: 'admin',
        request: 'get_user_metrics',
        page_id: adminPageId
      };
      WebSocketManager.sendRequest(WebSocketManager.protocol.createMessage(adminMsg))
        .then((data) => {
          handleConnectionsUpdate({ data: { data } });
        })
        .catch((err) => {
          showSnackbar('Failed to fetch connections: ' + (err.error || err.message || JSON.stringify(err)), 'error');
          console.error('[LiveConnections] Error fetching connections:', err);
        });
    } catch (error) {
      console.error('Error requesting connections update:', error);
    }
  }, [isConnected, adminPageId, showSnackbar, handleConnectionsUpdate]);

  // Setup WebSocket topic subscription and initial data load
  useEffect(() => {
    if (!isConnected) return;
    const unsubscribe = WebSocketManager.subscribeTopic('admin.connections', {
      onData: (data, rawMsg) => {
        handleConnectionsUpdate(data);
      },
      onAck: (ack, msg) => {
        console.log('[LiveConnections] Subscription ack received for admin.connections:', ack);
        // Send identity update after subscription is established
        try {
          // Gather all available identity details for this admin window
          const identityMsg = {
            type: 'identity',
            page_id: adminPageId,
            // Attempt to include window_id and device_id if available
            // Use window_id from WebSocketManager identity
            window_id: WebSocketManager.userIdentity?.window_id,
            device_id: navigator.userAgent || '', // fallback to user agent as device_id if nothing else
            // Optionally add user_id if available from context or auth (not available in this admin dashboard by default)
            // user_id: ...,
            // Add any other relevant details here
            // You can extend this with more fields as needed
          };
          // Remove undefined fields
          Object.keys(identityMsg).forEach(key => identityMsg[key] === undefined && delete identityMsg[key]);
          WebSocketManager.sendRequest(WebSocketManager.protocol.createMessage(identityMsg));
          console.log('[LiveConnections] Sent identity update after subscription:', identityMsg);
        } catch (err) {
          console.error('[LiveConnections] Failed to send identity update after subscription:', err);
        }
      },
      onError: (err, msg) => {
        showSnackbar('Subscription to admin.connections failed: ' + (err.error || err.message), 'error');
        console.error('[LiveConnections] Subscription error for admin.connections:', err);
      }
    });
    
    // Only request update if authenticated
    if (WebSocketManager.isAuthenticated) {
      requestConnectionsUpdate();
    }

    // Listen for auth success to request update if we weren't ready yet
    const onAuthSuccess = () => {
      console.log('[LiveConnections] Auth success detected, requesting connections update');
      requestConnectionsUpdate();
    };
    WebSocketManager.on('auth_success', onAuthSuccess);

    return () => {
      unsubscribe();
      WebSocketManager.off('auth_success', onAuthSuccess);
    };
  }, [isConnected, requestConnectionsUpdate, adminPageId, showSnackbar, handleConnectionsUpdate]);

  // Listen for status messages with user_metrics and update state
  useEffect(() => {
    if (!isConnected) return;
    WebSocketManager.on('message', handleConnectionsUpdate);
    WebSocketManager.on('raw_message', handleConnectionsUpdate);
    return () => {
      WebSocketManager.off('message', handleConnectionsUpdate);
      WebSocketManager.off('raw_message', handleConnectionsUpdate);
    };
  }, [isConnected, handleConnectionsUpdate]);

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleTimeString();
  };

  // Format connection duration
  const formatDuration = (connectedAt) => {
    const duration = Date.now() / 1000 - connectedAt;
    const minutes = Math.floor(duration / 60);
    const seconds = Math.floor(duration % 60);
    return `${minutes}m ${seconds}s`;
  };

  if (!isConnected) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader label="Connecting to WebSocket…" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-1 pt-0 px-4 md:px-6 pb-10">
      {/* Standardized page header (common across all pages) */}
      <div className="mb-6">
        <PageHeader
          title="Live WebSocket Connections"
          subtitle="Real-time monitoring of all WebSocket connections with hierarchical identity tracking"
          actions={
            <button
              type="button"
              onClick={requestConnectionsUpdate}
              className="btn btn-primary"
            >
              Refresh Now
            </button>
          }
        />
        <p className="text-sm text-fg-3">
          Last updated: {lastUpdate.toLocaleString()}
        </p>
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
        {/* Total Connections */}
        <div className="bg-bg-2 p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-fg-1">{connectionsData.totalConnections}</div>
          <div className="text-sm text-fg-2">Total Connections</div>
        </div>
        {/* Active Users with Internal/External breakdown (compact, no icons) */}
        <div className="bg-bg-2 p-4 rounded-lg shadow flex flex-col justify-center">
          <div className="text-2xl font-bold text-fg-1 text-center">{connectionsData.totalUsers}</div>
          <div className="text-sm text-fg-2 text-center mb-1">Active Users</div>
          <div className="text-xs text-fg-2 text-center mt-1">
            Internal: <span className="font-semibold text-fg-1">{connectionsData.totalInternalConnections}</span> &nbsp;|&nbsp; External: <span className="font-semibold text-fg-1">{connectionsData.totalExternalConnections}</span>
          </div>
        </div>
        {/* Devices */}
        <div className="bg-bg-2 p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-fg-1">{connectionsData.totalDevices}</div>
          <div className="text-sm text-fg-2">Devices</div>
        </div>
        {/* Windows */}
        <div className="bg-bg-2 p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-fg-1">{connectionsData.totalWindows}</div>
          <div className="text-sm text-fg-2">Windows</div>
        </div>
        {/* Pages */}
        <div className="bg-bg-2 p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-fg-1">{connectionsData.totalPages}</div>
          <div className="text-sm text-fg-2">Pages</div>
        </div>
        {/* Subscription Types */}
        <div className="bg-bg-2 p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-fg-1">{Object.keys(connectionsData.subscriptions).length}</div>
          <div className="text-sm text-fg-2">Subscription Types</div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="bg-bg-2 p-6 rounded-lg shadow mb-6">
        <h2 className="ds-h2 text-fg-1 mb-4">Performance Metrics</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-profit">
              {typeof connectionsData.performanceMetrics?.avgLatency === 'number' && !isNaN(connectionsData.performanceMetrics.avgLatency)
                ? connectionsData.performanceMetrics.avgLatency.toFixed(2)
                : '0.00'}ms
            </div>
            <div className="text-sm text-fg-2">Average Latency</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-accent">
              {typeof connectionsData.performanceMetrics?.messageVolume === 'number' && !isNaN(connectionsData.performanceMetrics.messageVolume)
                ? connectionsData.performanceMetrics.messageVolume
                : 0}
            </div>
            <div className="text-sm text-fg-2">Messages/Second</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-loss">
              {typeof connectionsData.performanceMetrics?.errorRate === 'number' && !isNaN(connectionsData.performanceMetrics.errorRate)
                ? (connectionsData.performanceMetrics.errorRate * 100).toFixed(2)
                : '0.00'}%
            </div>
            <div className="text-sm text-fg-2">Error Rate</div>
          </div>
        </div>
      </div>

      {/* Active Subscriptions */}
      <div className="bg-bg-2 p-6 rounded-lg shadow mb-6">
        <h2 className="ds-h2 text-fg-1 mb-4">Active Subscriptions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
          {Object.entries(connectionsData.subscriptions).map(([topic, count]) => (
            <div key={topic} className="flex justify-between items-center gap-2 p-3 bg-bg-3 dark:bg-bg-3 rounded">
              <span className="font-medium text-fg-1 min-w-0 truncate">{topic}</span>
              <span className="ds-badge xs info shrink-0">
                {count} subscribers
              </span>
            </div>
          ))}
          {Object.keys(connectionsData.subscriptions).length === 0 && (
            <div className="text-fg-2 italic col-span-full">No active subscriptions</div>
          )}
        </div>
      </div>

      {/* Hierarchical Connections Display */}
      <div className="bg-bg-2 p-6 rounded-lg shadow">
        <h2 className="ds-h2 text-fg-1 mb-4">Connected Users & Hierarchical Identity</h2>
        <div className="space-y-4">
          {Object.entries(connectionsData.users).map(([userId, userData]) => (
            <div key={userId} className="border border-[var(--divider)] rounded-lg p-4 bg-bg-2">
              {/* User Level */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  {/* Internal vs External icon and label */}
                  {userId.startsWith('service_') ? (
                    <span className="text-2xl mr-2">🔧</span>
                  ) : (
                    <span className="text-2xl mr-2">👤</span>
                  )}
                  <div>
                    {userId.startsWith('service_') ? (
                      <span className="font-semibold text-lg text-blue-700 dark:text-blue-300">Service: {userId.slice(8)}</span>
                    ) : (
                      <span className="font-semibold text-lg text-fg-1">User: {userId}</span>
                    )}
                    <div className="text-sm text-fg-2">
                      {userData.totalConnections} connections • Last seen: {formatTimestamp(userData.lastSeen)}
                    </div>
                  </div>
                </div>
                <div className="text-sm text-fg-2">
                  {Object.keys(userData.devices).length} devices
                </div>
              </div>

              {/* Device Level */}
              {/* Indent from lg only: three nested ml-8 + pl-4 levels burned ~144px
                  of a 375px phone (plus page/card padding), leaving the rows
                  unreadable. The tree still reads via the border-l rules. */}
              <div className="ml-0 lg:ml-8 space-y-3">
                {Object.entries(userData.devices).map(([deviceId, deviceData]) => (
                  <div key={deviceId} className="border-l-2 border-[var(--divider)] pl-2 lg:pl-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <span className="text-xl mr-2">🖥️</span>
                        <div>
                          <span className="font-medium text-fg-1">Device: {deviceId.substring(0, 20)}...</span>
                          <div className="text-sm text-fg-2">
                            {deviceData.metadata?.browser} • {deviceData.metadata?.os}
                          </div>
                        </div>
                      </div>
                      <div className="text-sm text-fg-2">
                        {deviceData.totalConnections} connections
                      </div>
                    </div>

                    {/* Window Level */}
                    <div className="ml-0 lg:ml-8 space-y-2">
                      {Object.entries(deviceData.windows).map(([windowId, windowData]) => (
                        <div key={windowId} className="border-l border-[var(--divider)] pl-2 lg:pl-4">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center">
                              <span className="text-lg mr-2">🪟</span>
                              <span className="font-medium text-fg-1">Window: {windowId.substring(0, 15)}...</span>
                            </div>
                            <div className="text-sm text-fg-2">
                              {windowData.totalConnections} pages
                            </div>
                          </div>

                          {/* Page Level */}
                          <div className="ml-0 lg:ml-8 space-y-1">
                            {Object.entries(windowData.pages).map(([pageId, pageData]) => (
                              <div key={pageId} className="flex items-center justify-between gap-2 py-2 px-3 bg-bg-3 dark:bg-bg-3 rounded">
                                <div className="flex items-center min-w-0">
                                  {/* Internal/external icon */}
                                  {pageData.connection_type === 'internal' || userId.startsWith('service_') ? (
                                    <span className="text-base mr-2 shrink-0">🔧</span>
                                  ) : (
                                    <span className="text-base mr-2 shrink-0">👤</span>
                                  )}
                                  <div className="min-w-0">
                                    <span className="block font-medium text-fg-1 truncate">Page: {pageId}</span>
                                    <div className="text-xs text-fg-2">
                                      <span className={`ds-badge xs mr-2 ${
                                        pageData.connection_type === 'internal' || userId.startsWith('service_')
                                          ? 'info'
                                          : 'profit'
                                      }`}>
                                        {pageData.connection_type === 'internal' || userId.startsWith('service_') ? 'Internal Service' : 'External User'}
                                      </span>
                                      Connected: {formatDuration(pageData.connected_at)}
                                    </div>
                                  </div>
                                </div>
                                <div className="text-sm shrink-0">
                                  <span className="ds-badge xs profit mr-2">
                                    {pageData.subscriptions.length} subs
                                  </span>
                                  <span className="text-fg-2 text-xs">
                                    {pageData.websocket_server}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {Object.keys(connectionsData.users).length === 0 && (
            <div className="text-center py-8 text-fg-2">
              No active connections
            </div>
          )}
        </div>
      </div>

      {/* Warning for missing fields */}
      {(connectionsData.missingDeviceId || connectionsData.missingWindowId || connectionsData.missingPageId) && (
        <div className="mb-4 p-3 bg-[var(--badge-warn-bg)] text-[var(--badge-warn-fg)] rounded">
          <strong>Warning:</strong> Some metrics are missing fields from the backend:&nbsp;
          {connectionsData.missingDeviceId && <span>device_id </span>}
          {connectionsData.missingWindowId && <span>window_id </span>}
          {connectionsData.missingPageId && <span>page_id </span>}
          <span>— counts may be inaccurate. Please update backend to include all fields.</span>
        </div>
      )}
    </div>
  );
};

export default LiveConnections;
